import React, { useState, useEffect, useRef } from 'react';
import styled, { stylesheet } from 'astroturf/react';
import { Icon } from 'js/design_system/Icon';

const CloseIcon = styled(Icon)`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 25px;
  height: 25px;
  font-size: 20px;
  cursor: pointer;
`;

const CheckIcon = styled(Icon)`
  display: inline-block;
  margin-left: 26px;
  margin-right: 8px;
  text-align: center;
  color: #219653;
`;

const ThanksContent = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const styles = stylesheet`
  .container {
    position: fixed;
    overflow: hidden;
    width: 670px;

    left: calc(50% - 560px);
    bottom: 3px;
    background: #f3f4fa;
    box-shadow: 0px -4px 10px rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    z-index: 10;
  }

  .short {
    min-height: 50px;
  }

  .expanded {
    min-height: 176px;
  }

  .prompt {
    display: inline-block;
    margin: 17px 16px 15px 24px;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.888889px;
  }

  .thanks {
    display: inline-block;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.888889px;
    font-weight: bold;
  }

  .option {
    button {
      cursor: pointer;
      color: #2553cc;
      border: 0px;
      background-color: inherit;
      padding: 0px;
    }
    font-weight: bold;
    display: inline-block;
    margin-right: 20px;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.888889px;
  }

  .center {
    text-align: center;
  }

  .inputWidth {
    width: 50%;
    margin: auto;
  }

  .reasonInput {
    background: #fbfbfb;
    border: 1px solid #d3d3d3;
    box-sizing: border-box;
    box-shadow: inset 0px 2px 2px rgba(0, 0, 0, 0.16);
    border-radius: 4px;
    height: 44px;
    width: 100%;
    margin-bottom: 12px;
    padding-left: 10px;
  }

  .selection {
    display: inline-block;
    font-weight: bold;
    font-size: 16px;
    line-height: 19px;
    letter-spacing: 0.3px;
    color: #219653;
  }

  .button {
    width: 100%;
    height: 43px;

    background: #2553cc;
    border-radius: 4px;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-transform: none;

    text-align: center;
    letter-spacing: 0.888889px;

    color: #ffffff;
  }
`;

interface Props {
  question: string;
  onSubmit: (sentiment: number, explanation: string) => void;
  whyNo?: boolean;
  reasonPlaceholder?: string;
}

export default function UserSentimentPopup(props: Props): React.ReactElement {
  // These correspond to the enum values for the UserWorkSentiment model
  // If the enum is updated these options should also be updated to reflect the change
  const {
    question,
    onSubmit,
    whyNo,
    reasonPlaceholder = 'Add a brief reason why...',
  } = props;
  const optionLabels = ['Yes', 'No', 'Kind of / It depends'];
  const explainNeeded = whyNo ? [1, 2] : [2];
  const [reasonText, setReasonText] = useState<string>('');
  const [selected, setSelected] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [closed, setClosed] = useState(false);

  const handleSubmit = () => {
    if (selected !== null) {
      const explanation = explainNeeded.includes(selected) ? reasonText : '';
      onSubmit(selected, explanation);
    }
  };

  useDidMount(() => {
    handleSubmit();
  }, [submitted]);

  return (
    <div
      className={
        selected !== null && explainNeeded.includes(selected) && !submitted
          ? styles.container + ' ' + styles.expanded
          : styles.container + ' ' + styles.short
      }
      hidden={closed}
    >
      <CloseIcon name="close" onClick={() => setClosed(true)} />
      {!submitted ? (
        <>
          <div className={styles.prompt}>{question}</div>
          {selected === null ? (
            optionLabels.map((label, index) => (
              <div key={index} className={styles.option}>
                <button
                  onClick={() => {
                    setSelected(index);
                    if (!explainNeeded.includes(index)) setSubmitted(true);
                  }}
                >
                  {label}
                </button>
              </div>
            ))
          ) : (
            <>
              <div className={styles.selection}>{optionLabels[selected]}</div>
              <div className={styles.inputWidth + ' ' + styles.center}>
                <input
                  type="text"
                  name="reason"
                  placeholder={reasonPlaceholder}
                  className={styles.reasonInput}
                  disabled={!explainNeeded.includes(selected)}
                  onChange={(e) => setReasonText(e.target.value)}
                />
                <button
                  className={styles.button + ' Button Button--lg Button--blue'}
                  onClick={() => setSubmitted(true)}
                >
                  Send Feedback to Academia.edu
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <ThanksContent>
          <CheckIcon name="check" />
          <div className={styles.thanks}>Thanks for sending your feedback!</div>
        </ThanksContent>
      )}
    </div>
  );
}

const useDidMount = (func: () => void, deps: Array<any>) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
    // Intentional here - passthrough of deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
